import { useMemo, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HeroReveal, Pressable, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { CardTornado, type TornadoCard } from '@/components/landing/listing'
import { DocThumb, ServiceFaq, StatsRow, UiFragment } from '@/components/landing/service'
import { BRAND, halftone } from '@/components/landing/service/halftone'
import { Highlight } from '@/components/landing/about/Highlight'
import type { SolutionData, SolutionStep } from '@/data/solutions'
import { MirroredSteps } from './MirroredSteps'
import { FADED, QUOTE, SolutionCta, SolutionStoryCard, splitLastWord, withHighlights, type SolutionLayoutProps } from './shared'

const TILE_COLORS = [BRAND.blue, BRAND.purple, BRAND.pink] as const
const DOC_ACCENTS = ['blue', 'purple', 'pink', 'green'] as const

/* ---- Tornado cards: the team's documents and its steps ---- */

function StepCardFront({ step, index }: { step: SolutionStep; index: number }) {
  return (
    <div
      className="relative flex h-full w-full flex-col justify-between overflow-hidden p-[1em] text-white"
      style={{ backgroundColor: TILE_COLORS[index % TILE_COLORS.length] }}
    >
      <span className="absolute inset-0" style={halftone(0.26, 6)} />
      <span className="relative font-[family-name:var(--font-landing-subdisplay)] text-[2.2em] font-light leading-none tracking-[-0.04em] opacity-80">
        {String(index + 1).padStart(2, '0')}
      </span>
      <span className="relative font-[family-name:var(--font-landing-display)] text-[0.85em] uppercase leading-[1.4]">
        {step.title}
      </span>
      <span className="relative flex flex-wrap gap-[0.35em]">
        {step.nodes.map((node, i) =>
          node.app ? (
            <span key={i} className="flex h-[1.9em] w-[1.9em] items-center justify-center bg-white">
              <OfficeAppIcon app={node.app} className="h-[1.5em] w-[1.5em]" />
            </span>
          ) : (
            <span key={i} className="flex h-[1.9em] items-center bg-white px-[0.5em] text-[0.6em] font-bold uppercase text-[var(--color-landing-gray-900)]">
              {node.label}
            </span>
          )
        )}
      </span>
    </div>
  )
}

function TeamCardFront({ solution }: { solution: SolutionData }) {
  const Icon = solution.icon
  return (
    <div className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-[#0a0a0a] p-[1em] text-white">
      <span className="absolute inset-0" style={halftone(0.16, 6)} />
      <Icon className="relative h-[2.2em] w-[2.2em]" strokeWidth={1.5} />
      <span className="relative font-[family-name:var(--font-landing-display)] text-[1em] uppercase leading-[1.35]">
        {solution.teamName}
      </span>
    </div>
  )
}

function CardBack({ index, solution }: { index: number; solution: SolutionData }) {
  const Icon = solution.icon
  return (
    <div
      className="relative flex h-full w-full items-center justify-center overflow-hidden text-white"
      style={{ backgroundColor: TILE_COLORS[index % TILE_COLORS.length] }}
    >
      <span className="absolute inset-0" style={halftone(0.3, 5)} />
      <Icon className="relative h-[3em] w-[3em] opacity-85" strokeWidth={1.25} />
    </div>
  )
}

/** Twelve cards: documents in four accents, the three steps and the team tile, interleaved. */
function buildCards(solution: SolutionData): TornadoCard[] {
  const docs = [...solution.documents, ...solution.documents]
  const cards: TornadoCard[] = []
  docs.forEach((doc, i) => {
    cards.push({
      id: `doc-${i}`,
      front: (
        <DocThumb
          kind={doc.kind}
          title={doc.title}
          accent={DOC_ACCENTS[(i + Math.floor(i / 4)) % DOC_ACCENTS.length]}
          className="h-full w-full shadow-[0_18px_30px_-18px_rgba(10,10,10,0.45)]"
        />
      ),
      back: <CardBack index={i} solution={solution} />,
    })
    if (i % 2 === 1) {
      const s = (i - 1) / 2
      const step = solution.steps[s]
      if (step) cards.push({ id: `step-${s}`, front: <StepCardFront step={step} index={s} />, back: <CardBack index={s + 1} solution={solution} /> })
      else cards.push({ id: `team-${s}`, front: <TeamCardFront solution={solution} />, back: <CardBack index={s} solution={solution} /> })
    }
  })
  return cards
}

/**
 * Works layout (blue2purple /works hero), used by Operations: split hero with
 * a small bottom-aligned headline and a 3D card tornado of the team's
 * documents, the challenge as a centred statement, the steps pinned and
 * mirrored (visual left, purple), the features as a white grid on a black
 * band with the stats, FAQ beside a sticky story card, and a dark CTA band.
 */
export function WorksLayout({ solution, story }: SolutionLayoutProps) {
  const cards = useMemo(() => buildCards(solution), [solution])
  const [featLead, featLast] = splitLastWord(solution.featuresHeadline)
  const stepsTitleId = `${solution.slug}-steps-title`

  return (
    <>
      {/* 1. Split hero: small headline bottom-left, card tornado right */}
      <section className="relative -mt-[72px] overflow-hidden">
        <div className="landing-container grid lg:min-h-[100svh] lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-10">
          <div className="flex flex-col justify-end pb-6 pt-[calc(72px+3.5rem)] lg:pb-20">
            <HeroReveal delay={0}>
              <p className="font-[family-name:var(--font-landing-display)] text-sm uppercase leading-[1.4] md:text-base">
                <span className="inline-block -rotate-1 bg-[var(--color-landing-secondary)] px-[0.5em] py-[0.25em] text-white">
                  {solution.teamLabel}
                </span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.08}>
              <h1 className="landing-text-display-lg mt-6 max-w-[16ch] text-[var(--color-landing-gray-900)] dark:text-white">
                <span className="block">{solution.headlineLines[0]}</span>{' '}
                <span className="block">{solution.headlineLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.16}>
              <p className={cn('landing-text-body-lg mt-6 max-w-[42ch] text-pretty', FADED)}>{solution.subheadline}</p>
            </HeroReveal>
            <HeroReveal delay={0.24}>
              <Pressable className="mt-10 inline-block">
                <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                  Book a demo
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Pressable>
            </HeroReveal>
          </div>

          <CardTornado cards={cards} className="md:h-[42rem] lg:h-[100svh] lg:-mr-[clamp(1.5rem,2vw,2rem)]" />
        </div>
      </section>

      {/* 2. The challenge as a centred statement, problem points in three columns */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="mx-auto max-w-[60rem]">
            <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
              {withHighlights(solution.problemHeadline, solution.problemHighlights, ['blue', 'pink'])}
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className={cn(QUOTE, 'mt-10 max-w-[60ch] text-pretty lg:mt-14')}>{solution.problemDescription}</p>
            </ScrollReveal>
          </div>
          <StaggerContainer amount={0.2} className="mx-auto mt-16 grid gap-10 md:grid-cols-3 md:gap-8 lg:mt-24 lg:max-w-[80%]">
            {solution.problemPoints.map((point, i) => {
              const Icon = point.icon
              return (
                <StaggerItem key={point.title} className="border-t border-[var(--color-landing-gray-900)] pt-6 dark:border-white/70">
                  <Icon aria-hidden className="h-9 w-9" style={{ color: TILE_COLORS[i % 3] }} strokeWidth={1.5} />
                  <h3 className="mt-8 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white">
                    {point.title}
                  </h3>
                  <p className={cn('landing-text-body mt-3 max-w-[36ch]', FADED)}>{point.description}</p>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* 3. Steps: pinned stage, mirrored, purple */}
      <section id="how-it-works" aria-labelledby={stepsTitleId} className="scroll-mt-24 landing-section !pt-0">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white lg:text-right">
            <span id={stepsTitleId}>
              How Templatean works for <Highlight color="purple">{solution.teamName.toLowerCase()} teams</Highlight>
            </span>
          </TextReveal>
        </div>
        <MirroredSteps
          accent="purple"
          steps={solution.steps.map((step) => ({
            title: step.title,
            description: step.description,
            visual: (active: boolean) => (
              <UiFragment kind={step.fragment ?? 'form'} title={step.title} industry="HR" active={active} />
            ),
          }))}
        />
      </section>

      {/* 4. Features: white capability grid on a black band, stats under it */}
      <section aria-labelledby="ops-features-title" className="landing-section-lg relative bg-[#0a0a0a] text-white dark:bg-black dark:ring-1 dark:ring-inset dark:ring-white/10">
        <div className="landing-container">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <TextReveal as="h2" className="landing-text-display-xl text-white">
              <span id="ops-features-title">
                {featLead} <Highlight color="pink">{featLast}</Highlight>
              </span>
            </TextReveal>
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-white/30 pl-5 text-white/70">{solution.featuresDescription}</p>
            </ScrollReveal>
          </div>

          <StaggerContainer amount={0.1} className="mt-16 grid gap-px bg-white/15 sm:grid-cols-2 xl:grid-cols-4 md:mt-20">
            {solution.features.map((feature, i) => {
              const Icon = feature.icon
              return (
                <StaggerItem
                  key={feature.title}
                  className="group relative flex min-h-[24rem] flex-col justify-between gap-12 overflow-hidden bg-white p-8 text-[var(--color-landing-gray-900)] dark:bg-[#16161f] dark:text-white md:p-10"
                  style={{ '--cell': [BRAND.blue, BRAND.purple, BRAND.pink, BRAND.blue][i % 4] } as CSSProperties}
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-[var(--cell)] transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [clip-path:inset(100%_0_0_0)] [@media(hover:hover)_and_(pointer:fine)]:group-hover:[clip-path:inset(0_0_0_0)]"
                  />
                  <Icon
                    aria-hidden
                    className="relative h-12 w-12 text-[var(--cell)] transition-colors duration-300 group-hover:text-white"
                    strokeWidth={1.25}
                  />
                  <div className="relative">
                    <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] transition-colors duration-300 group-hover:text-white md:text-xl">
                      {feature.title}
                    </h3>
                    <p className="landing-text-body-sm mt-4 text-[var(--color-landing-gray-600)] transition-colors duration-300 group-hover:text-white/90 dark:text-[var(--color-landing-gray-400)]">
                      {feature.description}
                    </p>
                    <ul className="mt-6 grid gap-1.5 border-t border-[var(--color-landing-gray-200)] pt-5 text-sm font-semibold transition-colors duration-300 group-hover:border-white/40 group-hover:text-white dark:border-[var(--color-landing-gray-700)]">
                      {feature.outcomes.map((outcome) => (
                        <li key={outcome} className="flex items-center gap-2.5">
                          <span aria-hidden className="block h-1.5 w-1.5 shrink-0 bg-[var(--cell)] group-hover:bg-white" />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </StaggerItem>
              )
            })}
          </StaggerContainer>

          <div className="mt-20 border-t border-white/20 pt-14 md:mt-28">
            <h3 className="sr-only">{solution.teamName} in numbers</h3>
            <StatsRow stats={solution.heroStats} />
          </div>
        </div>
      </section>

      {/* 5. FAQ with the story card held at the right */}
      <ServiceFaq
        items={solution.faq}
        aside={
          <div className="lg:sticky lg:top-28">
            <SolutionStoryCard solution={solution} story={story} />
          </div>
        }
      />

      {/* 6. Dark CTA band */}
      <SolutionCta solution={solution} tone="dark" />
    </>
  )
}

export default WorksLayout
